import * as Springroll from 'springroll';
import * as PIXI from 'pixi.js-legacy';
import PSound from 'pixi-sound';
import KeyboardJS from 'keyboardjs';

import { LAYOUT, GAMEPLAY } from './constants';

import { Scene } from './scenes/Scene';
import { TitleScene } from './scenes/01-title/titleScene';
import { AccessibilityPopover } from './scenes/02-accessibility/accessibilityPopover';
import { GameScene } from './scenes/04-game';
import { ResultScene } from './scenes/05-result';
import { Property, SafeScaleManager, ScaleEvent } from 'springroll';
import { GameTime, LoadContext, VOManager, wait, globals, FramerateTracker } from './utils';
import { FocusManager, fonts } from './ui';
import { UserProgress } from './UserProgress';
import { Settings } from './settings';


import { GoogleAnalytics } from  'springroll-google-analytics-plugin';
Springroll.Application.uses(new GoogleAnalytics());


// monkey patch a getter into BaseTexture to fix a minor bug in pixi's canvas fallback
Object.defineProperty(PIXI.BaseTexture.prototype, 'source', {
    get: function(this: PIXI.BaseTexture) {
        return (this.resource as PIXI.resources.ImageResource).source;
    }
});
// Monkey patch a canvas method to handle no params so that use of BitmapFont.from() won't break
// on older browsers - https://github.com/pixijs/pixi.js/issues/6939
// This won't be needed with PixiJS 5.4.0+
const oldTransform = CanvasRenderingContext2D.prototype.setTransform;
CanvasRenderingContext2D.prototype.setTransform = function(a?:number|DOMMatrix2DInit, b?: number, c?:number, d?:number, e?:number, f?:number) {
    if (a === undefined)
    {
        oldTransform.call(this, 1, 0, 0, 1, 0, 0);
    }
    oldTransform.call(this, a, b, c, d, e, f);
};

export class Game
{
    public width: number;
    public height: number;
    public app: Springroll.Application;
    public pixi: PIXI.Application;
    public scaleManager: SafeScaleManager;
    public isPaused:boolean = false;
    private frames: FramerateTracker;
    /** Accessibility settings, separate from Springroll Application settings */
    public settings:Settings;
    public vo: VOManager;
    public currentScene: Scene;
    private accessibilityPopover:AccessibilityPopover;
    private loader: PIXI.Sprite;
    private focus: FocusManager;
    private globalLoad: LoadContext;
    public userProgress: UserProgress;
    public videoElement: HTMLVideoElement;

    public voVolumeMultiplier: number = 0.2;
    public sfxVolumeMultiplier: number = 0.2;
    public musicVolumeMultiplier: number = 0.2;

    constructor()
    {
        (window as any).game = this;

        // window.localStorage.clear();

        const features = {
                captions: true,
                sound: true,
                music: true,
                vo: true,
                sfx: true,
                fullscreen: true,
            };

        this.update = this.update.bind(this);
        this.frames = new FramerateTracker();

        this.width = LAYOUT.MAX_WIDTH;
        this.height = LAYOUT.MAX_HEIGHT;
        this.app = new Springroll.Application(
        {
            features
        });

        //watch window for key inputs
        KeyboardJS.watch();

        this.focus = new FocusManager();

        this.videoElement = document.querySelector('#video');

        this.pixi = new PIXI.Application({ width: this.width, height: this.height, powerPreference: "high-performance", autoStart: false, transparent: true });
        document.getElementById('content').appendChild(this.pixi.view);
        globals.IS_CANVAS = this.pixi.renderer.type === PIXI.RENDERER_TYPE.CANVAS;

        // create a global loader to keep game level assets separate from being unloaded
        this.globalLoad = new LoadContext();
        // set up the loader and VO ASAP
        this.globalLoad.add('loaderBG', './assets/loader/loader-bg.jpg');
        this.globalLoad.add('voConfig', './assets/vo{size}.json');
        const loadComplete = this.globalLoad.load().then(() => {
            this.loader = new PIXI.Sprite(this.globalLoad.getAsset<PIXI.Texture>('loaderBG'));
            // block any clicks with the loader
            this.loader.interactive = true;
            this.pixi.stage.addChild(this.loader);

            // set up VO stuff
            this.vo = new VOManager(this.app, this.globalLoad.getAsset<any>('voConfig'), this.voVolumeMultiplier);
        });

        this.resize = this.resize.bind(this);
        this.scaleManager = new SafeScaleManager({width: this.width, height: this.height, safeWidth: LAYOUT.SAFE_WIDTH, safeHeight: LAYOUT.SAFE_HEIGHT, callback: this.resize});
        // hack in a fix for game sizing on iOS
        (this.scaleManager as any).resizer.getWindowResolution = () => {
            const width = document.documentElement.clientWidth;
            const height = document.documentElement.clientHeight;

            return { width, height };
        };

        // Subscribe to required springroll States.
        this.app.state.pause.subscribe((value) =>
        {
            this.frames.track = this.isPaused = value;
            // lock down all interaction
            this.pixi.stage.interactiveChildren = !value;
            // pause/unpause VO (including videos), and pause Pixi Sound in general to pause
            // music and sfx
            if (value)
            {
                this.vo?.pauseAll();
                PSound.pauseAll();
                KeyboardJS.pause();
            }
            else
            {
                this.frames.resetLast();
                // try a workaround to handle interruption on iOS, as they do their own thing
                // pixi-sound doesn't detect the 'interrupted' state when unpausing
                // note that this may not work all the time: https://github.com/CreateJS/SoundJS/issues/264
                if ((PSound.context.audioContext.state as any) === 'interrupted')
                {
                    PSound.context.audioContext.resume().then(() => {
                        // if it didn't unpause on it's own, try doing it in an input event,
                        // using pixi-sound's normal unlock code (after resetting its unlock state)
                        if (PSound.context.audioContext.state !== 'running')
                        {
                            const psContext = (PSound.context as any);
                            psContext._unlocked = false;
                            const unlock = () => {
                                document.removeEventListener("mousedown", unlock, true);
                                document.removeEventListener("touchstart", unlock, true);
                                document.removeEventListener("touchend", unlock, true);

                                // also try a resume here inside the input handler for good measure
                                if (psContext.audioContext.state === 'interrupted')
                                {
                                    psContext.audioContext.resume();
                                }
                                psContext._unlock();
                            };
                            document.addEventListener("mousedown", unlock, true);
                            document.addEventListener("touchstart", unlock, true);
                            document.addEventListener("touchend", unlock, true);
                        }
                    });
                }
                this.vo?.unpauseAll();
                PSound.resumeAll();
                KeyboardJS.resume();
            }
        });

        // Sound toggle from container.
        this.app.state.soundVolume.subscribe((value) =>
        {
            PSound.volumeAll = value;
            this.videoElement.volume = value * this.app.state.voVolume.value * this.voVolumeMultiplier;
        });

        // use VO volume for video element
        this.app.state.voVolume.subscribe((value) => {
            this.videoElement.volume = value * (this.app.state.soundVolume.value ?? 1) * this.voVolumeMultiplier;
        });

        // Captions - note, we'll get a warning about this but our caption manager will be
        // hooked up shortly, after the captions load
        // this.app.state.captionsMuted.subscribe((value) =>
        // {
        // });

        // add a extra state property for storying the current scene. Whenever the scene is changed, this class
        // will swap out the container attached to the stage
        this.app.state.scene = new Property(null);
        this.app.state.scene.subscribe(this.onChangeScene.bind(this));

        // wait for the app to be ready, then set the new scene
        this.app.state.ready.subscribe(async () =>
        {
            this.initializeSettings();
            // ensure that the loading screen has finished getting set up before we start loading
            // the title screen
            await loadComplete;
            this.app.state.scene.value = new TitleScene(this);
        });

        this.userProgress = {
            currentLevel: 0,
            batsRescued: 0
        };

        const data = window.localStorage.getItem('progress');
        if (data)
        {
            try
            {
                const json = JSON.parse(data);
                Object.assign(this.userProgress, json);
                // Use last saved level, or the level before the highest possible level.
                this.userProgress.currentLevel = Math.min( this.userProgress.currentLevel, GAMEPLAY.LEVELS.length - 2);
            }
            catch (e)
            {
            }
        }
    }

    // a method outside the constructor because we want to do it on a delay - we want to override
    // some of Springroll's default values that it doesn't set up immediately
    private initializeSettings()
    {
        this.settings = new Settings(this.app);

        const captionsDOM = document.getElementById('captions');

        fonts.listenForChanges(this.settings);

        const sizeChanged = (value: Settings.TextSize) => {
            this.setCaptionFont();
        }
        sizeChanged(this.settings.textSize.value);
        this.settings.textSize.subscribe(sizeChanged);

        const styleChanged = (value: Settings.TextStyle) => {
            captionsDOM.classList.remove('font-yellow-on-black', 'font-black-on-white', 'font-white-on-black');
            switch (value)
            {
                case Settings.TextStyle.YELLOW_ON_BLACK:
                    captionsDOM.classList.add('font-yellow-on-black');
                    break;
                case Settings.TextStyle.BLACK_ON_WHITE:
                    captionsDOM.classList.add('font-black-on-white');
                    break;
                case Settings.TextStyle.WHITE_ON_BLACK:
                    captionsDOM.classList.add('font-white-on-black');
                    break;
            }
        };
        styleChanged(this.settings.textStyle.value);
        this.settings.textStyle.subscribe(styleChanged);
    }

    public saveProgress()
    {
        window.localStorage.setItem( "progress", JSON.stringify(this.userProgress) );
    }

    // Called from game when a level is done.
    // Also called when temp interstitial screen is done (see below)
    public nextLevel(adjustment:number)
    {
        // Save last completed level (so we know where to start next time)
        this.saveProgress();
        this.userProgress.currentLevel += adjustment;
        if( this.userProgress.currentLevel < GAMEPLAY.LEVELS.length ) {
            // Go to game.
            this.app.state.scene.value = new GameScene(this, this.userProgress.currentLevel );
        } else {
            throw new Error(`Something went horribly wrong - could not apply level adjustment of ${adjustment} to ${this.userProgress.currentLevel - adjustment}`);
        }
    }

    public endLevel(success:boolean)
    {
        this.app.state.scene.value = new ResultScene(this, success, this.userProgress.currentLevel );
    }

    public returnToTitle()
    {
        if (this.userProgress.batsRescued >= GAMEPLAY.TOTAL_BATS)
        {
            this.userProgress.batsRescued = 0;
            this.saveProgress();
        }
        this.app.state.scene.value = new TitleScene(this);
    }

    run()
    {
        // to start the game up, register the update loop
        requestAnimationFrame(this.update);
    }

    onChangeScene(newScene:Scene, oldScene:Scene)
    {
        this.loader.visible = true;
        this.currentScene = null;
        this.frames.track = false;
        if (oldScene)
        {
            oldScene.cleanup();
            this.pixi.stage.removeChild(oldScene);
        }
        if (this.accessibilityPopover)
        {
            this.accessibilityPopover.cleanup();
            this.pixi.stage.removeChild( this.accessibilityPopover );
            this.accessibilityPopover = null;
        }

        // when a new scene is set, add it's container, then start it up
        newScene.preload().then(() =>
        {
            this.pixi.stage.addChildAt(newScene, 0);
            this.currentScene = newScene;
            this.loader.visible = false;
            // call start when loader is animated out
            newScene.start();
            this.frames.trackWithDelay(1000);
        });
    }

    update()
    {
        requestAnimationFrame(this.update);
        // if the game is paused, or there isn't a scene, we can skip rendering/updates
        if (this.isPaused || this.app.state.scene.value === null)
        {
            return;
        }
        const elapsed = this.frames.update();
        GameTime.update(elapsed);

        if (this.currentScene)
        {
            this.currentScene.update(elapsed);
        }

        this.pixi.render();
    }

    /**
     * Show the accessibility popover. Callback invoked when the popover is hidden.
     */
    showAccessibilityPopover(callback: ()=>void)
    {
        if (!this.accessibilityPopover)
        {
            this.accessibilityPopover = new AccessibilityPopover(this);
            this.frames.track = false;
            this.accessibilityPopover.preload().then( async ()=>{
                this.frames.trackWithDelay(500);
                this.pixi.stage.addChildAt( this.accessibilityPopover, 1 );
                this.accessibilityPopover.start();
                this.accessibilityPopover.visible = false;
                // wait to make sure the focus boxes get time to get set up
                await wait(1);
                this.accessibilityPopover.visible = true;
                this.accessibilityPopover.open();
                this.accessibilityPopover.once( 'close', ()=>{
                    this.pixi.stage.removeChild( this.accessibilityPopover );
                    callback();
                });
            });
        }
        else
        {
            this.frames.track = false;
            this.frames.trackWithDelay(500);
            this.pixi.stage.addChildAt( this.accessibilityPopover, 1 );
            this.accessibilityPopover.open();
            this.accessibilityPopover.once( 'close', ()=>{
                this.pixi.stage.removeChild( this.accessibilityPopover );
                callback();
            });
        }
    }

    resize(ev:ScaleEvent)
    {
        const {scaleRatio} = ev;
        // -- PIXI -- //
        const renderer = this.pixi.renderer;
        globals.SMALL_SCREEN = LAYOUT.MAX_HEIGHT * scaleRatio <= 600;

        renderer.view.style.width = `${LAYOUT.MAX_WIDTH * scaleRatio}px`;
        renderer.view.style.height = `${LAYOUT.MAX_HEIGHT * scaleRatio}px`;

        this.videoElement.style.width = `${LAYOUT.MAX_WIDTH * scaleRatio}px`;
        this.videoElement.style.height = `${LAYOUT.MAX_HEIGHT * scaleRatio}px`;

        this.setCaptionFont();
    }

    setCaptionFont()
    {
        let fontSize:number = 36;
        switch (this.settings?.textSize.value)
        {
            case Settings.TextSize.LARGE:
                fontSize = 42;
                break;
            case Settings.TextSize.MEDIUM:
                fontSize = 36;
                break;
            case Settings.TextSize.SMALL:
                fontSize = 28;
                break;
        }
        document.getElementById('captions').style.fontSize = `${fontSize * document.documentElement.clientHeight / 1080}px`;
    }

    public logEvent(action: string, label: string = "")
    {
        // now trigger an event:
        const googleAnalytics:GoogleAnalytics = this.app.getPlugin('GoogleAnalytics') as GoogleAnalytics;
        googleAnalytics.event("Echo Explorers", action, label);
    }
}
